import type { Toast as PrimeToast, ToastMessage } from "primereact/toast";
import { useImperativeHandle, useRef } from "react";
import type { ForwardedRef } from "react";

import { rootStylePt } from "../../utils/root-style-pt";
import { withDefaults } from "../../utils/with-defaults";
import { withFigmaToastIcons } from "./toast-icons";
import type { ToastProps, ToastViewProps } from "./types-toast";

// O Prime injeta `top/right: 20px` inline em `top-right`; `undefined` no `pt.root.style` apaga os dois e deixa a posição ao tema.
const PRIME_TOP_RIGHT_INLINE = { top: undefined, right: undefined } as const;

const withIcons = (message: ToastMessage | ToastMessage[]) =>
  Array.isArray(message) ? message.map(withFigmaToastIcons) : withFigmaToastIcons(message);

/**
 * ViewModel do `Toast` (`useToastComponent`, porque `useToast` é o hook público do contexto): liga a instância do Prime e expõe no `ref` a mesma API (`show`, `replace`, `clear`, `remove`,
 * `getElement`), com os ícones por severity do Figma como padrão em `show`/`replace`; sem JSX.
 */
export function useToastComponent(
  { className, style, pt, ...props }: ToastProps,
  ref: ForwardedRef<PrimeToast>,
): ToastViewProps {
  const toastRef = useRef<PrimeToast>(null);
  const rest = withDefaults({ position: "top-right" as const }, props);
  const position = rest.position;

  useImperativeHandle(ref, () => {
    const instance = () => toastRef.current as PrimeToast;
    return {
      show: (message) => instance().show(withIcons(message)),
      replace: (message) => instance().replace(withIcons(message)),
      clear: () => instance().clear(),
      remove: (message) => instance().remove(message),
      getElement: () => instance().getElement(),
    } as PrimeToast;
  });

  return {
    toastRef,
    className,
    style,
    pt: rootStylePt(pt, position === "top-right" ? { ...PRIME_TOP_RIGHT_INLINE, ...style } : style),
    rest,
  };
}
